import { CompanyType } from '@treadinc/horizon-api-spec';
import { uniqBy } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AppliedFiltersChipsCollectedData } from '~components/Filters/AppliedFiltersChips';
import { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import { AccountSelectedFilter, AccountTypesFilter } from '~constants/enums';

export type Filters = {
  connected_company?: { company_types?: CompanyType[] };
  departments?: string[];
  labels?: string[];
};

type AccountFilterKey =
  | Extract<keyof Filters, 'departments' | 'labels'>
  | 'connected_company.company_types';

type AppliedFiltersState = Record<
  AccountSelectedFilter,
  { accountFilterKey: AccountFilterKey; search: string }
>;

export const accountFilterKeyBySelectedFilter: Record<
  AccountSelectedFilter,
  AccountFilterKey
> = {
  [AccountSelectedFilter.DEPARTMENTS]: 'departments',
  [AccountSelectedFilter.COMPANY_TYPES]: 'connected_company.company_types',
  [AccountSelectedFilter.LABELS]: 'labels',
};

type FiltersAvailableOptionsState = Record<AccountSelectedFilter, FilterMenuOption[]>;

const getAppliedFiltersInitialState = () => {
  const initialState = Object.entries(accountFilterKeyBySelectedFilter).reduce(
    (obj, [filterKey, storeKey]) => {
      const key = filterKey as AccountSelectedFilter;

      obj[key] = {
        accountFilterKey: storeKey,
        search: '',
      };

      return obj;
    },
    {} as AppliedFiltersState,
  );

  return initialState;
};

const getAppliedFiltersChipsCollectedDataInitialState = (
  availableOptions: FiltersAvailableOptionsState,
) => {
  const collectedData = Object.entries(availableOptions).reduce((obj, [key, options]) => {
    obj[key as AccountSelectedFilter] = options;

    return obj;
  }, {} as AppliedFiltersChipsCollectedData);

  return collectedData;
};

const AVAILABLE_OPTIONS_STORAGE_KEY = 'accounts_availableOptions';
type AppliedFiltersStoredAvailableOptions = Partial<
  Record<AccountSelectedFilter, FilterMenuOption[]>
>;

const getAvailableOptionsInitialState = () => {
  const stored = localStorage.getItem(AVAILABLE_OPTIONS_STORAGE_KEY);
  const parsed = (
    stored ? JSON.parse(stored) : {}
  ) as AppliedFiltersStoredAvailableOptions;

  const initialState = {
    ...Object.values(AccountSelectedFilter).reduce((obj, filter) => {
      obj[filter] = parsed[filter] ?? [];

      return obj;
    }, {} as FiltersAvailableOptionsState),
  };

  return initialState;
};

export const useAccountFilters = () => {
  const [accountTypeFilter, setAccountTypeFilter] = useState<AccountTypesFilter>(
    AccountTypesFilter.CUSTOMER,
  );
  const accountTypeParam = useMemo(() => {
    if (accountTypeFilter === AccountTypesFilter.ALL) {
      return undefined;
    }

    return [accountTypeFilter];
  }, [accountTypeFilter]);

  const [appliedFilters, setAppliedFilters] = useState(getAppliedFiltersInitialState());
  const [availableOptions, setAvailableOptions] = useState(
    getAvailableOptionsInitialState(),
  );

  const [appliedFilterChipsCollectedData, setAppliedFilterChipsCollectedData] = useState(
    getAppliedFiltersChipsCollectedDataInitialState(availableOptions),
  );

  const updateAppliedFilterChipsCollectedData = useCallback(
    (filter: string, data: FilterMenuOption[]) => {
      const isValidFilter =
        accountFilterKeyBySelectedFilter[filter as AccountSelectedFilter];

      if (isValidFilter) {
        setAppliedFilterChipsCollectedData((state) => ({
          ...state,
          [filter]: uniqBy([...(state[filter] ?? []), ...data], (i) => i.value),
        }));
      }
    },
    [],
  );

  useEffect(() => {
    const options = Object.entries(availableOptions).reduce((obj, [key, options]) => {
      obj[key as AccountSelectedFilter] = options;

      return obj;
    }, {} as AppliedFiltersStoredAvailableOptions);

    localStorage.setItem(AVAILABLE_OPTIONS_STORAGE_KEY, JSON.stringify(options));
  }, [JSON.stringify(availableOptions)]);

  return {
    accountTypeFilter,
    setAccountTypeFilter,
    accountTypeParam,
    availableOptions,
    updateAvailableOptions: setAvailableOptions,
    appliedFilters,
    updateAppliedFilters: setAppliedFilters,
    appliedFilterChipsCollectedData,
    updateAppliedFilterChipsCollectedData,
  };
};
